import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Dialog, DialogActions, DialogContent } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useCallback, useRef, useState } from "react"
import { FormProvider, useForm, useWatch } from "react-hook-form"

import { useCreateGlTransaction } from "../../hooks/useCreateGlTransaction"
import { useGlTransactionFileUpload } from "../../hooks/useGlTransactionFileUpload"
import { type GlTransactionFormValues, glTransactionFormSchema } from "../../schemas/glTransactionForm"
import { toGlTransactionCreateInput } from "../../utils/glTransactionFormUtils"
import { DocumentAttachmentsSection } from "../DocumentAttachmentsSection"
import { GlTransactionDateSection } from "../GlTransactionDateSection"
import { RecurringSection } from "../RecurringSection"
import { TransactionLinesSection } from "../TransactionLinesSection"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { FormCheckbox } from "@/components/UiKit/FormControls/FormCheckbox"
import { FormTextField } from "@/components/UiKit/FormControls/FormTextField"
import { GlTransactionRepeatByUnit, GlTransactionRowType } from "@/graphql/codegen/graphql"

interface AddGlTransactionDialogProps {
  onClose: () => void
  onSuccess?: () => void
}

const DEFAULT_GL_TRANSACTION_FORM_VALUES: GlTransactionFormValues = {
  transactionLines: [
    { type: GlTransactionRowType.Debit, glAccountId: null, amount: null },
    { type: GlTransactionRowType.Credit, glAccountId: null, amount: null },
  ],
  transactionDate: "",
  description: "",
  note: "",
  isRecurring: false,
  repeatEndDate: "",
  repeatEvery: 1,
  repeatByUnit: GlTransactionRepeatByUnit.Month,
}

export const AddGlTransactionDialog: FC<AddGlTransactionDialogProps> = ({ onClose, onSuccess }) => {
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { createGlTransaction, isCreating } = useCreateGlTransaction()
  const { uploadFilesForTransactions, isUploading } = useGlTransactionFileUpload()

  const handleFileAdd = useCallback((file: File) => {
    setPendingFiles((prev) => [...prev, file])
  }, [])

  const handleFileRemove = useCallback((index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const methods = useForm<GlTransactionFormValues>({
    resolver: zodResolver(glTransactionFormSchema),
    defaultValues: DEFAULT_GL_TRANSACTION_FORM_VALUES,
    mode: "onChange",
  })

  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty, isSubmitting },
  } = methods

  const canSubmit = isValid && isDirty && !isSubmitting && !isCreating && !isUploading

  const isRecurring = useWatch({ control, name: "isRecurring" })

  const handleFormSubmit = useCallback(
    async (formValues: GlTransactionFormValues) => {
      const input = toGlTransactionCreateInput(formValues)
      const transactionIds = await createGlTransaction(input)
      if (transactionIds) {
        if (pendingFiles.length > 0) {
          await uploadFilesForTransactions(pendingFiles, transactionIds)
        }
        onSuccess?.()
        onClose()
      }
    },
    [createGlTransaction, uploadFilesForTransactions, pendingFiles, onSuccess, onClose]
  )

  const handleClose = useCallback(() => {
    if (isDirty) {
      setIsCancelDialogOpen(true)
    } else {
      onClose()
    }
  }, [isDirty, onClose])

  if (isCancelDialogOpen) {
    return <CancelDialog onClose={() => setIsCancelDialogOpen(false)} onConfirm={onClose} />
  }

  return (
    <Dialog open onClose={handleClose} maxWidth='md' fullWidth data-testid='gl-transaction.form-dialog'>
      <DialogHeader title='Add General Ledger Entry' onClose={handleClose} />
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <DialogContent className='flex flex-col gap-6 pt-4'>
            <TransactionLinesSection />

            <Box className='grid grid-cols-3 items-start gap-4'>
              <GlTransactionDateSection showRecurringLabel testIdPrefix='gl-transaction.form-dialog' />

              <Box className='pt-5'>
                <FormCheckbox
                  control={control}
                  name='isRecurring'
                  label='Recurring Event'
                  data-testid='gl-transaction.form-dialog.recurring-checkbox'
                />
              </Box>
            </Box>

            {isRecurring && <RecurringSection />}

            <FormTextField
              control={control}
              name='description'
              label='Description'
              required
              slotProps={{ htmlInput: { maxLength: 30 } }}
              data-testid='gl-transaction.form-dialog.description'
            />

            <FormTextField
              control={control}
              name='note'
              label='Notes'
              multiline
              rows={3}
              slotProps={{ htmlInput: { maxLength: 100 } }}
              data-testid='gl-transaction.form-dialog.note'
            />

            <DocumentAttachmentsSection
              files={pendingFiles}
              onAdd={handleFileAdd}
              onRemove={handleFileRemove}
              fileInputRef={fileInputRef}
            />
          </DialogContent>

          <DialogActions className='px-6 pb-6'>
            <Button appearance='outlined' onClick={handleClose} data-testid='gl-transaction.form-dialog.cancel-button'>
              Cancel
            </Button>
            <Box className='flex items-center gap-3'>
              <Button
                appearance='text'
                startIcon={<AddOutlined />}
                onClick={() => fileInputRef.current?.click()}
                rootElementName='button'
                data-testid='gl-transaction.form-dialog.add-another-file-button'
              >
                Add Another File
              </Button>
              <Button
                type='submit'
                appearance='contained'
                disabled={!canSubmit}
                data-testid='gl-transaction.form-dialog.save-button'
              >
                Save
              </Button>
            </Box>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  )
}
